import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Star, SlidersHorizontal } from "lucide-react";
import api from "@/lib/api";
import { ProductGrid } from "@/components/store/ProductRow";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";

const SORTS = [["featured", "Recommended"], ["best_selling", "Best Selling"], ["price_asc", "Price: Low to High"],
  ["price_desc", "Price: High to Low"], ["newest", "Newest"], ["top_rated", "Highest Rated"]];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0 });
  const [cats, setCats] = useState([]);
  const [brands, setBrands] = useState([]);
  const [price, setPrice] = useState([0, 200]);
  const [loading, setLoading] = useState(true);
  const page = parseInt(params.get("page") || "1");
  const q = params.get("q") || "";
  const category = params.get("category") || "all";
  const sort = params.get("sort") || "featured";
  const brand = params.get("brand") || "";
  const minRating = params.get("min_rating") || "";

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/brands").then((r) => setBrands(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const sp = new URLSearchParams();
    if (q) sp.set("q", q);
    if (category !== "all") sp.set("category", category);
    sp.set("sort", sort);
    if (params.get("sale")) sp.set("sale", "true");
    if (brand) sp.set("brand", brand);
    if (minRating) sp.set("min_rating", minRating);
    sp.set("min_price", price[0]); sp.set("max_price", price[1]);
    sp.set("page", page); sp.set("limit", "30");
    api.get(`/products?${sp.toString()}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [q, category, sort, brand, minRating, price, page, params]);

  const setP = (k, v) => { const p = new URLSearchParams(params); v ? p.set(k, v) : p.delete(k); p.delete("page"); setParams(p); };
  const totalPages = Math.min(20, Math.ceil(data.total / 30));
  const shownTo = Math.min(page * 30, data.total);
  const shownFrom = data.total === 0 ? 0 : (page - 1) * 30 + 1;

  return (
    <div className="bg-[#F1F3F6] min-h-screen">
      <div className="max-w-[1500px] mx-auto px-4 py-4 flex gap-4">
        {/* Filters */}
        <aside className="hidden lg:block w-60 shrink-0 space-y-3">
          <div className="bg-white rounded-md border border-zinc-200 p-4">
            <h3 className="font-semibold text-sm flex items-center gap-2 mb-3"><SlidersHorizontal size={14} />Category</h3>
            <div className="space-y-0.5 max-h-56 overflow-auto">
              <button onClick={() => setP("category", "")} className={`block w-full text-left text-[13px] px-2 py-1.5 rounded ${category === "all" ? "bg-[#FFF3E6] text-[#FF6A1A] font-medium" : "hover:bg-zinc-50 text-zinc-600"}`}>All Categories</button>
              {cats.map((c) => (
                <button key={c.key} onClick={() => setP("category", c.query_category || c.key)} className={`block w-full text-left text-[13px] px-2 py-1.5 rounded ${category === (c.query_category || c.key) ? "bg-[#FFF3E6] text-[#FF6A1A] font-medium" : "hover:bg-zinc-50 text-zinc-600"}`} data-testid={`filter-cat-${c.key}`}>{c.name}</button>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-md border border-zinc-200 p-4">
            <h3 className="font-semibold text-sm mb-3">Price (USD)</h3>
            <Slider value={price} onValueChange={setPrice} min={0} max={200} step={5} data-testid="price-slider" />
            <div className="flex justify-between text-xs text-zinc-500 mt-2 font-mono"><span>${price[0]}</span><span>${price[1]}</span></div>
          </div>
          <div className="bg-white rounded-md border border-zinc-200 p-4">
            <h3 className="font-semibold text-sm mb-3">Customer Rating</h3>
            {[4.5, 4, 3].map((r) => (
              <button key={r} onClick={() => setP("min_rating", minRating === String(r) ? "" : String(r))} className={`flex items-center gap-1 w-full px-2 py-1.5 rounded text-sm ${minRating === String(r) ? "bg-[#FFF3E6]" : "hover:bg-zinc-50"}`} data-testid={`filter-rating-${r}`}>
                {[1, 2, 3, 4, 5].map((s) => <Star key={s} size={13} className={s <= r ? "fill-[#FFA200] text-[#FFA200]" : "text-zinc-300"} />)}
                <span className="text-xs text-zinc-500 ml-1">& up</span>
              </button>
            ))}
          </div>
          {brands.length > 0 && (
            <div className="bg-white rounded-md border border-zinc-200 p-4">
              <h3 className="font-semibold text-sm mb-3">Brand</h3>
              <div className="space-y-0.5 max-h-48 overflow-auto">
                {brands.slice(0, 20).map((b) => (
                  <button key={b} onClick={() => setP("brand", brand === b ? "" : b)} className={`block w-full text-left text-[13px] px-2 py-1 rounded ${brand === b ? "bg-[#FFF3E6] text-[#FF6A1A]" : "hover:bg-zinc-50 text-zinc-600"}`}>{b}</button>
                ))}
              </div>
            </div>
          )}
        </aside>

        <div className="flex-1 min-w-0">
          <div className="bg-white rounded-md border border-zinc-200 px-4 py-2.5 flex items-center justify-between mb-3">
            <p className="text-sm text-zinc-600"><b className="text-zinc-900">{shownFrom}–{shownTo}</b> of <b className="text-zinc-900">{data.total.toLocaleString()}{data.total >= 30 ? "+" : ""}</b> results{q && <> for "<b>{q}</b>"</>}</p>
            <select value={sort} onChange={(e) => setP("sort", e.target.value)} className="text-sm border border-zinc-200 rounded px-2 py-1.5 outline-none" data-testid="sort-select">
              {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">{Array(10).fill(0).map((_, i) => <div key={i} className="aspect-[3/4] bg-white rounded-md animate-pulse" />)}</div>
          ) : data.items.length === 0 ? (
            <div className="bg-white rounded-md border border-zinc-200 py-24 text-center text-zinc-500">
              <p>No products match your filters.</p>
              <Button variant="outline" className="mt-4" onClick={() => setParams({})} data-testid="clear-filters">Clear all filters</Button>
            </div>
          ) : <ProductGrid products={data.items} />}

          {totalPages > 1 && (
            <div className="flex justify-center gap-1.5 mt-6">
              {Array.from({ length: totalPages }).map((_, i) => (
                <button key={i} onClick={() => setP("page", String(i + 1))} className={`w-9 h-9 rounded text-sm font-medium ${page === i + 1 ? "bg-[#FF6A1A] text-white" : "bg-white border border-zinc-200 text-zinc-600 hover:border-zinc-400"}`} data-testid={`page-${i + 1}`}>{i + 1}</button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
